<template>
	<v-card class="metric-cumul-card" :class="{ 'metric-cumul-card--mobile': mobile }" flat border>
		<v-card-title class="metric-cumul-card__title">
			<v-icon>{{ icon }}</v-icon>
			{{ title }}
		</v-card-title>

		<v-card-text class="metric-cumul-card__body">
			<div v-for="row in rows" :key="row.base" class="metric-cumul-card__row">
				<span class="metric-cumul-card__row-label">
					<v-icon size="small">{{ row.icon }}</v-icon>
					{{ row.label }}
					<v-tooltip v-if="row.anomaly" :attach="overlayTarget" location="top">
						<template #activator="{ props: tooltipProps }">
							<v-icon v-bind="tooltipProps" color="warning" size="small">warning</v-icon>
						</template>
						<span>{{ t('DeliveredAboveOrderedAnomaly') }}</span>
					</v-tooltip>
				</span>

				<ProgressBarRenderer v-if="row.hasObjective" class="metric-cumul-card__bar" :params="row.params" />

				<template v-else>
					<span class="metric-cumul-card__plain-value">{{ row.formattedValue }}</span>
					<span class="metric-cumul-card__plain-note">{{ t('NoObjective') }}</span>
				</template>
			</div>
		</v-card-text>

		<v-card-actions v-if="showReturns" class="metric-cumul-card__footer">
			<v-chip variant="tonal" readonly>
				<v-icon start>keyboard_return</v-icon>
				{{ t('Returns') }}
				<strong class="ml-1">{{ formatMetric(totals.returned[metric], metric) }}</strong>
			</v-chip>
		</v-card-actions>
	</v-card>
</template>

<script setup>
import { inject, computed } from 'vue';
import { useTranslator } from '@/composables/useTranslator';
import { formatPercent, formatMetric } from '@/utils/core/format';
import { buildGauge } from '@/utils/domain/objectiveGauge';
import { METRIC, BASE } from '@/services/directOrderService';
import ProgressBarRenderer from '@/components/agGrid/ProgressBarRenderer.vue';

const props = defineProps({
	title: { type: String, default: '' },
	icon: { type: String, default: 'paid' },
	metric: { type: String, default: METRIC.VALUE },
	totals: { type: Object, required: true },
	objectives: { type: Object, default: () => ({ ordered: { objective: 0, hasObjective: false }, delivered: { objective: 0, hasObjective: false } }) },
	showOrdered: { type: Boolean, default: true },
	showDelivered: { type: Boolean, default: true },
	showReturns: { type: Boolean, default: false },
	mobile: { type: Boolean, default: false },
});

const t = useTranslator();
const overlayTarget = inject('overlayTarget', null);

function buildRow(base, icon, label, anomaly) {
	const objective = props.objectives[base] ?? {};
	const value = props.totals[base][props.metric];
	const gauge = buildGauge(value, objective.hasObjective ? objective.objective : 0);
	const formattedValue = formatMetric(value, props.metric);

	return {
		base,
		icon,
		label,
		anomaly,
		formattedValue,
		hasObjective: gauge.hasObjective,
		params: {
			value,
			valueFormatted: formattedValue,
			objective: objective.objective,
			objectiveFormatted: `${t('Objective')} ${formatMetric(objective.objective, props.metric)} ·`,
			objectiveSuffix: formatPercent(gauge.percentage / 100),
			objectiveSuffixClass: gauge.classColor,
			barColor: gauge.color,
		},
	};
}

const rows = computed(() => {
	const list = [];
	if (props.showOrdered) list.push(buildRow(BASE.ORDERED, 'shopping_cart', t('Ordered'), false));
	if (props.showDelivered) list.push(buildRow(BASE.DELIVERED, 'fact_check', t('Delivered'), props.totals.anomaly[props.metric]));
	return list;
});
</script>

<style scoped lang="scss">
@use '@/styles/variables' as *;

.metric-cumul-card {
	height: 100%;
	display: flex;
	flex-direction: column;
	padding: 12px;

	&__title {
		display: flex;
		align-items: center;
		gap: 8px;
		font-size: $font-size;
		font-weight: 600;
		padding: 4px;
	}

	& &__body {
		display: grid;
		grid-template-columns: 120px minmax(0, max-content) 1fr;
		column-gap: 16px;
		row-gap: 4px;
		flex: 1 1 auto;
		padding: 8px;
		padding-bottom: 8px !important;
	}

	&__row {
		display: contents;
	}

	&__row-label {
		display: flex;
		align-items: center;
		gap: 4px;
		align-self: center;
		color: rgba(var(--v-theme-on-surface), 0.7);
	}

	&__bar {
		grid-column: 2 / span 2;
		align-self: center;
		min-width: 0;
	}

	&__plain-value {
		font-weight: 600;
		font-size: $font-size-extra-large !important;
		text-align: right;
		align-self: baseline;
	}

	&__plain-note {
		font-size: $font-size-small !important;
		color: rgba(var(--v-theme-on-surface), 0.68);
		align-self: baseline;
		white-space: nowrap;
	}

	&__footer {
		padding-top: 0;
		justify-content: flex-start;
		border-top: thin dashed rgba(var(--v-border-color), var(--v-border-opacity));
	}

	&--mobile {
		.metric-cumul-card__body {
			grid-template-columns: 1fr;
			row-gap: 16px;
		}

		.metric-cumul-card__row {
			display: block;
		}

		.metric-cumul-card__row-label {
			margin-bottom: 4px;
		}

		.metric-cumul-card__bar {
			grid-column: unset;
		}

		.metric-cumul-card__plain-value,
		.metric-cumul-card__plain-note {
			display: block;
			text-align: left;
		}
	}
}
</style>
